import { Box, Button, Card, CardContent, Chip, Container, Grid, Stack, Typography, Avatar, CardActionArea } from '@mui/material';
import { ArrowForward, Campaign, FactCheck, HowToReg, School } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../../shared/api';
import type { Notice, Post, Alumni } from '../../shared/types';
import { SCHOOL } from '../../shared/constants';
import { formatDate } from '../../shared/utils';

interface HomeData {
  latestNotices: Notice[];
  latestPosts: Post[];
  stats: { students: number; teachers: number; classes: number; established: number };
  alumni: Alumni[];
}

export default function HomePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['public-home'],
    queryFn: async () => (await api.get<HomeData>('/public/home')).data,
  });

  return (
    <Box>
      {/* Hero */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1E3A8A 0%, #172554 100%)',
        color: '#fff', pt: { xs: 8, md: 12 }, pb: { xs: 10, md: 14 },
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip label={`Established ${SCHOOL.established}`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', mb: 2, fontWeight: 600 }} />
              <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.05, fontSize: { xs: 36, md: 56 } }}>
                Where Curiosity<br />Meets Excellence
              </Typography>
              <Typography variant="h6" sx={{ mt: 3, opacity: 0.85, fontWeight: 400, maxWidth: 560 }}>
                A modern learning community in Pune for students from LKG through Class 12. Holistic education, top-tier faculty, and a thriving alumni network.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button size="large" variant="contained" color="secondary" component={RouterLink} to="/alumni" endIcon={<ArrowForward />}>
                  Apply for Admission
                </Button>
                <Button size="large" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} component={RouterLink} to="/result-check">
                  Check Result
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, p: 3,
              }}>
                <Stack spacing={2}>
                  {[
                    { icon: <School />, label: 'Students', value: data?.stats.students ?? '—' },
                    { icon: <HowToReg />, label: 'Faculty', value: data?.stats.teachers ?? '—' },
                    { icon: <Campaign />, label: 'Classes (LKG–12)', value: data?.stats.classes ?? '—' },
                    { icon: <FactCheck />, label: 'Years of Excellence', value: new Date().getFullYear() - SCHOOL.established },
                  ].map((s) => (
                    <Stack key={s.label} direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}>{s.icon}</Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Quick access cards */}
      <Container maxWidth="lg" sx={{ mt: -6, mb: 8 }}>
        <Grid container spacing={3}>
          {[
            { title: 'Notices', icon: <Campaign />, body: 'Stay updated with the latest school announcements', link: '/alumni', cta: 'View notices' },
            { title: 'Admissions Open', icon: <HowToReg />, body: 'Applications for 2026-27 are now being accepted', link: '/alumni', cta: 'Apply now' },
            { title: 'Check Result', icon: <FactCheck />, body: 'Enter your roll number and DOB to view your result', link: '/result-check', cta: 'Check now' },
          ].map((c) => (
            <Grid item xs={12} md={4} key={c.title}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea component={RouterLink} to={c.link} sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main' }}>{c.icon}</Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{c.body}</Typography>
                    </Box>
                  </Stack>
                  <Typography variant="button" color="primary" sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {c.cta} <ArrowForward fontSize="small" />
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Latest notices + posts */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>Latest Notices</Typography>
              <Button component={RouterLink} to="/alumni" endIcon={<ArrowForward />}>View all</Button>
            </Stack>
            <Stack spacing={2}>
              {(data?.latestNotices ?? []).map((n) => (
                <Card key={n.id}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" color="primary" label={n.audience.replace('_', ' ')} />
                      <Typography variant="caption" color="text.secondary">{formatDate(n.publishedAt)}</Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{n.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {n.body.replace(/\*\*/g, '')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
              {!isLoading && (data?.latestNotices ?? []).length === 0 && (
                <Typography color="text.secondary">No notices yet.</Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>Updates</Typography>
              <Button component={RouterLink} to="/alumni" endIcon={<ArrowForward />}>All posts</Button>
            </Stack>
            <Stack spacing={2}>
              {(data?.latestPosts ?? []).map((p) => (
                <Card key={p.id}>
                  <CardContent>
                    <Chip size="small" label={p.type.replace('_', ' ')} color="secondary" sx={{ mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{p.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {p.body}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Alumni spotlight */}
      <Box sx={{ bgcolor: '#F1F5F9', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Alumni Spotlight</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 4 }}>
            Our graduates are leaders, scientists, artists and innovators — across the world.
          </Typography>
          <Grid container spacing={3}>
            {(data?.alumni ?? []).map((a) => (
              <Grid item xs={12} sm={6} md={3} key={a.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mb: 2, fontSize: 24, fontWeight: 700 }}>
                      {a.name.split(' ').map((s) => s[0]).join('').slice(0, 2)}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{a.name}</Typography>
                    <Typography variant="caption" color="text.secondary">Batch {a.batchYear}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>{a.currentRole}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{a.achievements}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About summary */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>About Baseras</Typography>
            <Typography color="text.secondary" sx={{ mt: 2, fontSize: 16 }}>
              Founded in {SCHOOL.established}, Baseras School is a co-educational institution committed to academic
              excellence and character development. Our integrated curriculum, dedicated faculty, and modern infrastructure
              empower students to thrive in a rapidly changing world.
            </Typography>
            <Button component={RouterLink} to="/about" variant="contained" sx={{ mt: 3 }} endIcon={<ArrowForward />}>
              Learn more about us
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800"
              alt="Campus"
              sx={{ width: '100%', borderRadius: 3, boxShadow: 4 }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
